import { getPrismicLocale, Locales } from "@/lib/i18n";
import { resolveCategories } from "@/lib/i18n/functions";
import {
  getCollectionFeaturedImages,
  retrieveProduct,
  retrieveProductByHandleStrict,
  retrieveProductStrict,
} from "@/lib/medusa/data/products";
import { getAllByIDs, getAllByType, getAllByUids, getByID, getDocument } from "@/lib/prismic";
import { createClient } from "@/prismicio";
import {
  CategorieDocument,
  InspirationDocument,
  ProductDocument,
  ProductDocumentDataCategoriesItem,
  TablesDocument,
} from "@/prismicio-types";
import { EnrichedProduct, ProductDocumentWithTranslation, ProductImage } from "@/types";
import { filter, ImageField, isFilled } from "@prismicio/client";
import { StoreProduct } from "@medusajs/types";
import { getField } from "@/lib/prismic/data";
import { extractKeyTextValue } from "@/lib/utils";

/**
 * Fetches the product document from the French locale based on the provided slugs because we need a the French version as the categories/related products/collection     products are only available in French locale.
 * @param slugs - The slugs of the product to fetch from French locale.
 * @param currentLocale - The current locale of the user.
 * @returns - The product document from the French locale or null if the current locale is French.
 */
export const getFrenchProduct = async (medusaId: string, currentLocale: Locales) => {
  if (currentLocale === "fr") {
    return null;
  }
  try {
    const product = (await getAllByType("product", "fr", {
      filters: [filter.at("my.product.medusaId", medusaId)],
    })) as ProductDocument[] | null;
    return product?.[0];
  } catch (error) {
    console.error("Error fetching French locale images:", error);
    return null;
  }
};

/**
 * Retrieves images or featured image from the French product document.
 * @param frenchProduct - The product document in French locale.
 * @param returnType - Specifies whether to return all images or just the featured image.
 * @returns - An array of images or a single featured image.
 */
export const getImagesFromFrenchLocale = (frenchProduct: ProductDocument, returnType: "images" | "featuredImage") => {
  if (returnType === "images") return frenchProduct.data.images;
  return frenchProduct.data.featuredImage;
};

/**
 * Fetches related product documents from Prismic based on an array of product IDs .
 * @param productIds - An array of product IDs to fetch.
 * @param locale - The current locale of the user.
 * @returns - An array of related product documents or null if none found.
 */
export const getRelatedPrismicProducts = async (productIds: string[] | null, locale: Locales) => {
  if (!productIds || !productIds[0]) return null;

  const frenchProducts = (await getAllByIDs(productIds, {
    lang: getPrismicLocale("fr"),
  })) as ProductDocument[] | null;

  if (locale === "fr") return { relatedProducts: frenchProducts || [], frenchProducts };

  const localeProducts = (await getAllByType("product", locale, {
    filters: [
      filter.any(
        "my.product.uid",
        frenchProducts?.map(
          (p) => p.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid || p.uid
        ) || []
      ),
    ],
  })) as ProductDocument[] | null;

  if (!localeProducts) return { relatedProducts: [], frenchProducts: [] };

  const relatedProducts = (frenchProducts || [])
    ?.map((frenchProduct) => {
      const localized = localeProducts.find(
        (lp) => frenchProduct.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid === lp.uid
      );

      if (!localized) {
        return null;
      }

      return {
        ...localized,
        data: {
          ...localized.data,
          images: frenchProduct.data.images,
          featuredImage: frenchProduct.data.featuredImage,
          productCollection: frenchProduct.data.productCollection,
          image_full_size_card: frenchProduct.data.image_full_size_card,
        },
      };
    })
    .filter((p): p is ProductDocument => p !== null);

  return { relatedProducts, frenchProducts };
};

/**
 * This function retrives the categories for the related products as we need the category title of related products to display in the product card.
 * @param relatedProducts - An array of  product documents.
 * @param locale - The current locale of the user.
 * @returns - an array of category documents or an empty array if none found.
 */
export const getRelatedPrismicProductCategories = async (relatedProducts: ProductDocument[], locale: Locales) => {
  const categoriesArrays = await Promise.all(
    relatedProducts.map(async (relatedProduct) => {
      const categoriesUids = getField("categories", relatedProduct)
        ?.map((category: ProductDocumentDataCategoriesItem) => {
          return "uid" in category.category ? category.category?.uid : false;
        })
        .filter(Boolean);

      const frenchCategories = (await getAllByUids("categorie", categoriesUids, "fr", {
        fetch: ["categorie.title", "categorie.medusaId"],
      })) as CategorieDocument[];

      if (locale === "fr") return frenchCategories || [];

      const localeCategoriesUids = frenchCategories.map(
        (frenchCategory) =>
          frenchCategory.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid ||
          frenchCategory.uid
      );

      const localeCategories = (await getAllByUids("categorie", localeCategoriesUids, locale, {
        fetch: ["categorie.title", "categorie.medusaId"],
      })) as CategorieDocument[];

      return localeCategories || [];
    })
  );

  return categoriesArrays;
};

/**
 * Fetches products by category ID, excluding the current product.
 * @param categoryId - The ID of the category to fetch products from.
 * @param limit - The maximum number of products to return.
 * @param excludeProductUid - The UID of a product to exclude from the results.
 * @param locale - The current locale of the user.
 * @returns - An array of product documents or an empty array if none found.
 */
export const getProductsByCategoryId = async (
  categoryId: string,
  limit: number = 2,
  excludeProductUid: string,
  locale: Locales
) => {
  const client = createClient();

  const filters = [filter.at("my.product.categories.category", categoryId)];

  if (excludeProductUid) {
    filters.push(filter.not("my.product.uid", excludeProductUid));
  }

  const res = await client.getByType("product", {
    filters,
    pageSize: limit,
    fetch: [
      "product.title",
      "product.images",
      "product.featuredImage",
      "product.medusaId",
      "product.productCollection",
      "product.image_full_size_card",
    ],
    lang: getPrismicLocale("fr"),
    orderings: {
      field: "document.first_publication_date",
      direction: "desc",
    },
  });

  if (locale === "fr") return res.results;

  const targetPrismicLang = getPrismicLocale(locale);

  const targetUIDs = res.results
    .map((product) => product.alternate_languages.find((alt) => alt.lang === targetPrismicLang)?.uid)
    .filter(Boolean) as string[];

  const localisedProducts = await client.getAllByUIDs("product", targetUIDs, { lang: getPrismicLocale(locale) });

  if (localisedProducts.length === 0) {
    return [];
  }

  const mergedResults = localisedProducts.map((currentProduct) => {
    const frenchProduct = res.results.find(
      (product) => product.alternate_languages.find((alt) => alt.lang === targetPrismicLang)?.uid === currentProduct.uid
    );

    return {
      ...currentProduct,
      data: {
        ...currentProduct.data,
        medusaId: currentProduct.data.medusaId,
        images: frenchProduct?.data?.images,
        featuredImage: frenchProduct?.data?.featuredImage,
        productCollection: frenchProduct?.data?.productCollection,
        image_full_size_card: frenchProduct?.data?.image_full_size_card,
      },
    } as ProductDocument;
  });

  return mergedResults;
};

/**
 * This function retrives products from multiple categories and enriches them with category information.
 * Its useful for displaying products for 'This products might interest you' section on product page if the product is not a collection otherwise we will just display the collection products.
 * It handles both single and multiple categories, ensuring a minimum number of products are returned.
 * It handles both case of multiple categories and single category if we have only one category it will try to fetch max no of products from that category if its not able to satisfy the max no of products it will try to fetch from the parent category. At the very least 3 products will always be returned.
 * In every case it tries to fetch max of 8 products if not possible it will try to fetch from parent categories.
 * If there are multiple categories it will try to fetch from products from first 4 categories, max of 8 products 2 per category,
 * Smartly it will try to fetch from parent categories if the products are not enough and the no of categories are even/odd.
 * @param categories
 * @param excludeUid
 * @param locale
 * @returns
 */
export const getProductsByCategories = async (
  categories: CategorieDocument[],
  excludeUid: string,
  locale: Locales
): Promise<EnrichedProduct[]> => {
  const MAX_CATEGORIES = 4;
  const TARGET_PRODUCTS = 8;
  const MIN_PRODUCTS = 3;

  const categoriesToProcess = categories.slice(0, MAX_CATEGORIES);
  const enrichedResults: EnrichedProduct[] = [];

  const targetPrismicLang = getPrismicLocale(locale);

  // Get the equivalent category UIDs for the target locale
  const targetUIDs = categories
    .map((category) => category.alternate_languages.find((alt) => alt.lang === targetPrismicLang)?.uid)
    .filter(Boolean) as string[];

  // Fetch the translated categories
  const prismicCategories = (await getAllByUids("categorie", targetUIDs, locale)) as CategorieDocument<string>[];

  // Create a mapping from original category ID to translated category
  const categoryMapping = new Map<string, CategorieDocument>();
  categoriesToProcess.forEach((originalCategory, index) => {
    const translatedCategory = prismicCategories.find((prismicCat) => prismicCat.uid === targetUIDs[index]);
    if (translatedCategory) {
      categoryMapping.set(originalCategory.id, translatedCategory);
    } else {
      // Fallback to original category if translation not found
      categoryMapping.set(originalCategory.id, originalCategory);
    }
  });

  // Helper function to get category info for enrichment
  const getCategoryInfo = (originalCategory: CategorieDocument) => {
    const categoryToUse = categoryMapping.get(originalCategory.id) || originalCategory;
    return {
      uid: categoryToUse.uid,
      id: categoryToUse.id,
      title: categoryToUse.data.title || "Untitled Category",
    };
  };

  // CASE 1: only one category
  if (categoriesToProcess.length === 1) {
    const category = categoriesToProcess[0];
    const categoryInfo = getCategoryInfo(category);

    let products = await getProductsByCategoryId(
      locale === "fr"
        ? category.id
        : category.alternate_languages.find((alt) => alt.lang === getPrismicLocale("fr"))?.id || category.id,
      TARGET_PRODUCTS,
      excludeUid,
      locale
    );

    if (products.length >= MIN_PRODUCTS) {
      const enriched = enrichProducts(products, categoryInfo);
      return enriched;
    }

    const parent = category.data.parentCategorie;
    if (products.length < MIN_PRODUCTS && isFilled.contentRelationship(parent)) {
      const parentId = parent.id;
      const productsNeeded = MIN_PRODUCTS - products.length;
      const parentProducts = await getProductsByCategoryId(parentId, productsNeeded, excludeUid, locale);
      products = [...products, ...parentProducts];
    }

    const enriched = enrichProducts(products, categoryInfo);
    return enriched;
  }

  // Multiple categories case
  const numCategories = categoriesToProcess.length;
  const baseProductsPerCategory = Math.floor(TARGET_PRODUCTS / numCategories);
  const extraProducts = TARGET_PRODUCTS % numCategories;

  const categoryResults: {
    category: { uid: string; id: string; title: string };
    products: ProductDocument[];
    targetCount: number;
    actualCount: number;
  }[] = [];

  for (let i = 0; i < categoriesToProcess.length; i++) {
    const category = categoriesToProcess[i];
    const { id } = category;
    const categoryInfo = getCategoryInfo(category);

    const targetForThisCategory = baseProductsPerCategory + (i < extraProducts ? 1 : 0);

    let products = await getProductsByCategoryId(id, targetForThisCategory, excludeUid, locale);

    // Try parent category if we don't have enough
    const parent = category.data.parentCategorie;
    if (products.length < targetForThisCategory && isFilled.contentRelationship(parent)) {
      const parentId = parent.id;
      const parentProducts = await getProductsByCategoryId(
        parentId,
        targetForThisCategory - products.length,
        excludeUid,
        locale
      );
      products = [...products, ...parentProducts];
    }

    categoryResults.push({
      category: categoryInfo,
      products,
      targetCount: targetForThisCategory,
      actualCount: products.length,
    });
  }

  const totalProductsFound = categoryResults.reduce((sum, result) => sum + result.actualCount, 0);

  // If we still need more products, try to get them from parent categories
  if (totalProductsFound < TARGET_PRODUCTS) {
    const productsNeeded = TARGET_PRODUCTS - totalProductsFound;

    for (const result of categoryResults) {
      if (productsNeeded <= 0) break;

      const category = categoriesToProcess.find((cat) => cat.id === result.category.id);
      const parent = category?.data.parentCategorie;

      if (isFilled.contentRelationship(parent)) {
        const parentId = parent.id;
        const additionalProducts = await getProductsByCategoryId(parentId, productsNeeded, excludeUid, locale);

        result.products = [...result.products, ...additionalProducts];
        result.actualCount = result.products.length;
      }
    }
  }

  for (const result of categoryResults) {
    const enriched = enrichProducts(result.products, result.category);
    enrichedResults.push(...enriched);
  }

  return enrichedResults;
};

/**
 * Enriches product documents with category information and returnes a new array of enriched products.
 * @param prismicProducts - An array of product documents from Prismic.
 * @param category - An object containing category information (uid, title, id).
 * @returns - An array of enriched product objects, each containing Prismic product data and category information.
 */
const enrichProducts = (
  prismicProducts: ProductDocument[],
  category: { uid: string; title: string; id: string }
): EnrichedProduct[] => {
  return prismicProducts.map((product) => {
    return {
      prismic: {
        uid: product.uid,
        title: product.data.title,
        images: product.data.images,
        featuredImage: product.data.featuredImage,
        medusaId: product.data.medusaId,
        image_full_size_card: product.data.image_full_size_card || false,
        isCollection: product.data.productCollection || false,
        alternate_languages: product.alternate_languages || [],
      },
      category,
    };
  });
};

/**
 * This function retrieves the basic product data needed at build time for the product page.
 * It fetches the French product document if the current locale is not French and retrives the product images from Meudusa
 * @param product - The prismic product document to process.
 * @param resolution - The current resolution object.
 * @returns - An object containing basic product data : Prismic Product ( will always be from french locale ), Product Featured image from medusa and boolean indicating if the product is a collection or not.
 */
export const getProductBasicData = async (product: ProductDocument, resolution: Resolution) => {
  const productWithTranslation = product as Partial<ProductDocumentWithTranslation>;
  const translatedFrenchProduct =
    productWithTranslation.translation?.lang === getPrismicLocale("fr") ? productWithTranslation.translation : null;

  const frenchProduct =
    resolution.locale !== "fr"
      ? translatedFrenchProduct ||
        (await getFrenchProduct(extractKeyTextValue(product.data.medusaId), resolution.locale))
      : product;

  const sharedProductData = frenchProduct || product;
  const isCollection = Boolean(sharedProductData.data.productCollection);
  const medusaProduct = product.id.startsWith("medusa_")
    ? product.uid
      ? await retrieveProductByHandleStrict(product.uid)
      : null
    : await retrieveProductStrict(
        extractKeyTextValue(sharedProductData.data.medusaId)
      );

  const productsWhichAreCollection = (await getAllByType("product", "fr", {
    filters: [filter.at("my.product.productCollection", true)],
  })) as ProductDocument[] | null;

  let parentCollection: ProductDocument | undefined = undefined;

  parentCollection = productsWhichAreCollection?.find((collectionProduct) => {
    return collectionProduct.data.products.some((item) => {
      if (isFilled.contentRelationship(item.product)) {
        return item.product.uid === frenchProduct?.uid;
      }
      return false;
    });
  });

  // If not in French, try to find the localized parentCollection
  if (resolution.locale !== "fr" && parentCollection?.alternate_languages?.length) {
    const targetPrismicLang = getPrismicLocale(resolution.locale);

    const matchingAlt = parentCollection.alternate_languages.find((alt) => alt.lang === targetPrismicLang);

    if (matchingAlt) {
      const localizedParentCollection = await getDocument("product", matchingAlt?.uid || "", resolution.locale);
      if (localizedParentCollection) {
        parentCollection = localizedParentCollection as ProductDocument;
      }
    }
  }

  return {
    frenchProduct,
    isCollection,
    parentCollection,
    medusaProduct,
  };
};

const PRODUCT_IMAGE_TOKEN_STOPWORDS = new Set([
  "avec",
  "boite",
  "box",
  "de",
  "doos",
  "for",
  "het",
  "les",
  "met",
  "of",
  "the",
  "van",
]);

const normalizeProductImageText = (value: string | undefined | null) => {
  if (!value) return "";

  try {
    value = decodeURIComponent(value);
  } catch {
    // Keep malformed URLs sortable instead of dropping the image.
  }

  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const getProductImageTokens = (medusaProduct: StoreProduct) => {
  const source = normalizeProductImageText(`${medusaProduct.handle || ""} ${medusaProduct.title || ""}`);

  return source
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 2 && !/^\d+$/.test(token) && !PRODUCT_IMAGE_TOKEN_STOPWORDS.has(token));
};

const getSortedMedusaImages = (medusaProduct: StoreProduct) => {
  const tokens = getProductImageTokens(medusaProduct);
  const preferredToken = tokens.at(-1);

  return [...(medusaProduct.images || [])].sort((imageA, imageB) => {
    const imageAText = normalizeProductImageText(imageA.url);
    const imageBText = normalizeProductImageText(imageB.url);

    const imageAScore =
      (preferredToken && imageAText.includes(preferredToken) ? 100 : 0) +
      tokens.filter((token) => imageAText.includes(token)).length;
    const imageBScore =
      (preferredToken && imageBText.includes(preferredToken) ? 100 : 0) +
      tokens.filter((token) => imageBText.includes(token)).length;

    if (imageAScore !== imageBScore) {
      return imageBScore - imageAScore;
    }

    return (imageA.rank || 0) - (imageB.rank || 0);
  });
};

/**
 * This function processes the product images for both single products and collections.
 * If the product is not a collection , it will set the featured image for the product from Medusa and fetch the rest of the images from Prismic ( French locale as product images are only available in French locale ) and returns a object containing the product images and an empty array for collection products and categories.,
 * If the product is a collection, it will set the featured image from the Prismic product document ( French locale ) and get all the collection product ids and fetch other products related to that collection from Prismic and then it will fetch the categories for those collection products from Prismic and finally it will fetch the collection products from Medusa and resolve the images for those products which will act as the rest of the images for the image gallery ( featured image comes from prismic in this case ). Lastly it will return an object containing the product images and the collection products and categories.
 *
 * @param product - The prismic product document to process.
 * @param frenchProduct - The French version of the product document.
 * @param medusaProduct - The Medusa product object ( which contains the images ).
 * @param isCollection - A boolean indicating if the product is part of a collection.
 * @param resolution - The current resolution object.
 * @returns - An object containing processed product images and related collection data.
 */
export const processProductImages = async (
  product: ProductDocument,
  frenchProduct: ProductDocument | null | undefined,
  medusaProduct: StoreProduct | undefined,
  isCollection: boolean,
  resolution: Resolution
) => {
  const productImages: ProductImage[] = [];
  let collectionProductsFromMedusa: StoreProduct[] = [];
  let collectionProductsFromPrismic: ProductDocument[] | null = [];
  let collectionProductCategoriesFromPrismic: CategorieDocument[][] | null = [];

  const addProductImage = (image: ProductImage) => {
    if (!image.url || productImages.some((existingImage) => existingImage.url === image.url)) return;

    productImages.push({
      ...image,
      isFeatured: image.isFeatured && !productImages.some((existingImage) => existingImage.isFeatured),
    });
  };

  const prismicImages =
    resolution.locale === "fr" || !frenchProduct
      ? product.data?.images || []
      : getImagesFromFrenchLocale(frenchProduct, "images") || [];

  const featuredImageData =
    resolution.locale === "fr" || !frenchProduct
      ? product.data.featuredImage
      : (getImagesFromFrenchLocale(frenchProduct, "featuredImage") as ImageField);

  const addPrismicGalleryImages = () => {
    if (!Array.isArray(prismicImages) || prismicImages.length === 0) return;

    prismicImages.forEach((img, index: number) => {
      if (img.image.url) {
        addProductImage({
          url: img.image.url,
          alt: img.image.alt || `Product image ${index + 1}`,
          source: "prismic",
          isFeatured: productImages.length === 0,
        });
      }
    });
  };

  const addPrismicFeaturedImage = () => {
    if (!featuredImageData || !isFilled.image(featuredImageData)) return;

    addProductImage({
      url: featuredImageData.url,
      alt: featuredImageData.alt || "Product featured image",
      source: "prismic",
      isFeatured: productImages.length === 0,
    });
  };

  const medusaImages = medusaProduct?.images?.length ? getSortedMedusaImages(medusaProduct) : [];
  const [primaryMedusaImage, ...secondaryMedusaImages] = medusaImages;

  if (primaryMedusaImage?.url) {
    addProductImage({
      url: primaryMedusaImage.url,
      alt: medusaProduct?.title || "Product image",
      source: "medusa",
      isFeatured: true,
    });
  }

  if (isCollection) {
    addPrismicFeaturedImage();
    addPrismicGalleryImages();
  } else {
    addPrismicGalleryImages();
    addPrismicFeaturedImage();
  }

  // Additional Medusa images stay after the Prismic image so image 2 can be the Prismic editorial asset.
  secondaryMedusaImages.forEach((image) => {
    if (!image.url) return;

    addProductImage({
      url: image.url,
      alt: medusaProduct?.title || "Product image",
      source: "medusa",
      isFeatured: productImages.length === 0,
    });
  });

  if (isCollection) {
    const collectionProductIds =
      (frenchProduct &&
        frenchProduct.data.products
          .map((item) => item.product)
          .filter(isFilled.contentRelationship)
          .map((docLink) => docLink.id)
          .filter((id): id is string => typeof id === "string")) ||
      [];

    const result = await getRelatedPrismicProducts(collectionProductIds, resolution.locale);

    const frenchProducts = result && result.frenchProducts;
    collectionProductsFromPrismic = result && result.relatedProducts;

    const collectionProductMedusaIds =
      frenchProducts?.map((product) => extractKeyTextValue(product.data.medusaId)) || [];

    collectionProductCategoriesFromPrismic =
      collectionProductsFromPrismic &&
      (await getRelatedPrismicProductCategories(
        resolution.locale === "fr" ? collectionProductsFromPrismic : frenchProducts || [],
        resolution.locale
      ));

    collectionProductsFromMedusa = (
      await Promise.all(collectionProductMedusaIds.map((medusaId) => retrieveProduct(medusaId)))
    ).filter((product): product is StoreProduct => Boolean(product));

    const collectionFeaturedImages = await getCollectionFeaturedImages(collectionProductsFromMedusa);
    productImages.push(...collectionFeaturedImages);
  }

  return {
    productImages,
    collectionProductsFromPrismic,
    collectionProductCategoriesFromPrismic,
  };
};

/**
 * This function retrives the related products data from the french product document we show this data in the 'Articles similaires' section on the product page.
 * It fetches the related product ids from the French product document and then fetches the related products from Prismic.
 * It also fetches the categories for those related products from Prismic.
 * @param frenchProduct - The French product document to fetch related products from.
 * @param resolution  - The current resolution object containing locale information.
 * @returns - An object containing related product data.
 */
export const getRelatedProductsData = async (
  frenchProduct: ProductDocument | null | undefined,
  resolution: Resolution
) => {
  const relatedProductIds =
    frenchProduct &&
    frenchProduct.data.relatedProducts
      .map((item) => item.relatedProduct)
      .filter(isFilled.contentRelationship)
      .map((docLink) => docLink.id);

  const productIds = relatedProductIds !== undefined ? relatedProductIds : null;

  const result = await getRelatedPrismicProducts(productIds, resolution.locale);

  const frenchProducts = result && result.frenchProducts;
  const relatedProductsFromPrismic = result && result.relatedProducts;

  const relatedProductsCategoriesFromPrismic =
    relatedProductsFromPrismic &&
    (await getRelatedPrismicProductCategories(
      resolution.locale === "fr" ? relatedProductsFromPrismic : frenchProducts || [],
      resolution.locale
    ));

  return {
    relatedProductIds,
    relatedProductsFromPrismic,
    relatedProductsCategoriesFromPrismic,
  };
};

/**
 * This function retrieves the products from the same categories and excludes the current product. We show this data in 'This products might interest you' section on the product page if the product is not a collection
 * @param frenchProduct - The French product document to fetch categories from.
 * @param productUid - The unique identifier of the product.
 * @param resolution - The current resolution object containing locale information.
 * @returns - An array of enriched products data.
 */
export const getCategoryProductsData = async (
  frenchProduct: ProductDocument | null | undefined,
  productUid: string,
  resolution: Resolution
) => {
  const categories = await resolveCategories(frenchProduct, "fr");
  return await getProductsByCategories(categories, productUid, resolution.locale);
};

export const getProductTables = async (frenchproduct: ProductDocument, locale: Locales): Promise<TablesDocument[]> => {
  let productTables: TablesDocument[];

  const frenchproductTables = await Promise.all(
    frenchproduct.data.linkedTables.map(async ({ table }) => {
      if (isFilled.contentRelationship(table)) {
        return await getByID(table.id, {
          lang: getPrismicLocale("fr"),
        });
      }
    })
  );

  productTables = frenchproductTables.filter((doc): doc is TablesDocument => doc !== undefined && doc !== null);

  if (locale === "fr") return productTables;

  const localeTableUIDs = productTables
    .map((table) => table.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid)
    .filter(Boolean) as string[];

  const localeTables = (await getAllByUids("tables", localeTableUIDs, locale)) as TablesDocument[] | null;

  productTables = (
    localeTables?.map((localeTable) => {
      const frenchTable = productTables.find(
        (table) =>
          table.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid === localeTable.uid
      );

      if (!frenchTable) return undefined;

      return {
        ...localeTable,
        data: {
          ...localeTable.data,
          featured_image: frenchTable.data.featured_image,
        },
      };
    }) || []
  ).filter((doc): doc is TablesDocument => !!doc);

  return productTables;
};

export const getProductInspirations = async (
  frenchproduct: ProductDocument,
  locale: Locales
): Promise<InspirationDocument[]> => {
  let productInspirations: InspirationDocument[];

  const frenchproductInspirations = await Promise.all(
    frenchproduct.data.linkedInspirations.map(async ({ inspiration }) => {
      if (isFilled.contentRelationship(inspiration)) {
        return await getByID(inspiration.id, {
          lang: getPrismicLocale("fr"),
        });
      }
    })
  );

  productInspirations = frenchproductInspirations.filter(
    (doc): doc is InspirationDocument => doc !== undefined && doc !== null
  );

  if (locale === "fr") return productInspirations;

  const localeInspirationUIDs = productInspirations
    .map((inspiration) => inspiration.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid)
    .filter(Boolean) as string[];

  const localeInspirations = (await getAllByUids("inspiration", localeInspirationUIDs, locale)) as
    | InspirationDocument[]
    | null;

  productInspirations = (
    localeInspirations?.map((localeInspiration) => {
      const frenchTable = productInspirations.find(
        (inspiration) =>
          inspiration.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid ===
          localeInspiration.uid
      );

      if (!frenchTable) return undefined;

      return {
        ...localeInspiration,
        data: {
          ...localeInspiration.data,
          featured_image: frenchTable.data.featured_image,
        },
      };
    }) || []
  ).filter((doc): doc is InspirationDocument => !!doc);

  return productInspirations;
};

export const extractTextFromPrismicContent = (content: any[]): string => {
  return content
    .filter((item) => item.type === "paragraph" && item.text)
    .map((item) => item.text.trim())
    .join(" ")
    .replace(/\r\n/g, " ")
    .replace(/\r/g, " ")
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};
